import { Box } from '@mui/material';
import { useState } from 'react';
import jsPDF from 'jspdf';
import { TableTitleBox } from '../../common/components';
import { useGerencialesStatistics } from './hooks/useGerencialesStatistics';
import { GERENCIALES_REPORT_OPTIONS, GERENCIALES_REPORT_TYPE } from './constants/gerencialesConstants';
import GerencialesFilters from './components/GerencialesFilters';
import ContractDetailsReport from './components/contract-details/ContractDetailsReport';

const GerencialesStatistics = () => {
  const { loading, error, results, header, reportType, reload, handleReportTypeChange } =
    useGerencialesStatistics();
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Valida si hay datos para exportar.
   * @param {Object|null} reportResults - Resultados del reporte.
   * @returns {boolean} Indica si hay data disponible.
   */
  const hasReportData = (reportResults) => {
    if (!reportResults) return false;
    return Object.values(reportResults).some(
      (value) => Array.isArray(value) && value.length > 0
    );
  };

  /**
   * Convierte un grafico SVG a PNG para embebido en PDF.
   * @param {string} chartId - ID del contenedor del grafico.
   * @returns {Promise<string|null>} DataURL PNG o null si falla.
   */
  const getChartPng = async (chartId) => {
    const container = document.getElementById(chartId);
    if (!container) return null;
    const svg = container.querySelector('svg');
    if (!svg) return null;

    const { width, height } = svg.getBoundingClientRect();
    const clonedSvg = svg.cloneNode(true);
    clonedSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clonedSvg.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');

    const originalElements = svg.querySelectorAll('*');
    const clonedElements = clonedSvg.querySelectorAll('*');
    clonedElements.forEach((element, index) => {
      const originalElement = originalElements[index];
      if (!originalElement) return;
      const computedStyle = window.getComputedStyle(originalElement);
      const styleText = Array.from(computedStyle)
        .map((prop) => `${prop}:${computedStyle.getPropertyValue(prop)}`)
        .join(';');
      element.setAttribute('style', styleText);
    });

    const resolvedWidth = Math.max(Math.round(width), 1);
    const resolvedHeight = Math.max(Math.round(height), 1);

    if (!clonedSvg.getAttribute('width')) {
      clonedSvg.setAttribute('width', `${resolvedWidth}`);
    }
    if (!clonedSvg.getAttribute('height')) {
      clonedSvg.setAttribute('height', `${resolvedHeight}`);
    }
    if (!clonedSvg.getAttribute('viewBox')) {
      clonedSvg.setAttribute('viewBox', `0 0 ${resolvedWidth} ${resolvedHeight}`);
    }

    const svgData = new XMLSerializer().serializeToString(clonedSvg);
    const encodedSvg = window.btoa(unescape(encodeURIComponent(svgData)));
    const svgUrl = `data:image/svg+xml;base64,${encodedSvg}`;

    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const scale = 2;
        const canvas = document.createElement('canvas');
        canvas.width = resolvedWidth * scale;
        canvas.height = resolvedHeight * scale;
        const context = canvas.getContext('2d');
        if (context) {
          context.fillStyle = '#ffffff';
          context.fillRect(0, 0, canvas.width, canvas.height);
          context.drawImage(image, 0, 0, canvas.width, canvas.height);
        }
        resolve(canvas.toDataURL('image/png'));
      };
      image.onerror = () => resolve(null);
      image.src = svgUrl;
    });
  };

  /**
   * Genera la estructura de secciones para el PDF de detalle de contratos.
   * @param {Object} reportResults - Resultados del reporte.
   * @param {Object|null} reportHeader - Header del reporte.
   * @returns {Array<Object>} Secciones con tabla y grafico.
   */
  const buildContractDetailsPdfSections = (reportResults, reportHeader) => {
    const totalContracts =
      reportHeader?.total_contracts != null ? reportHeader.total_contracts : 0;
    const updatedAt = reportHeader?.updated_at_date || 'Sin fecha';

    return [
      {
        title: 'Contratos por tipo de cuota',
        subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        columns: [
          { key: 'label', label: 'Tipo cuota', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ],
        rows: reportResults.fee_type || [],
        chartId: 'gerenciales-contract-details-fee-type',
        totalLabel: 'Total contratos',
        totalCount: totalContracts,
      },
      {
        title: 'Contratos por tipo de posesion',
        subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        columns: [
          { key: 'label', label: 'Tipo posesion', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ],
        rows: reportResults.possession || [],
        chartId: 'gerenciales-contract-details-possession',
        totalLabel: 'Total contratos',
        totalCount: totalContracts,
      },
      {
        title: 'Contratos por tipo de cliente',
        subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        columns: [
          { key: 'label', label: 'Tipo cliente', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ],
        rows: reportResults.client_type || [],
        chartId: 'gerenciales-contract-details-client-type',
        totalLabel: 'Total contratos',
        totalCount: totalContracts,
      },
      {
        title: 'Contratos por tipo de cuota y posesion',
        subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        columns: [
          { key: 'fee_type', label: 'Tipo cuota', align: 'left' },
          { key: 'possession', label: 'Tipo posesion', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ],
        rows: reportResults.fee_type_possession || [],
        chartId: 'gerenciales-contract-details-fee-type-possession',
        totalLabel: 'Total contratos',
        totalCount: totalContracts,
      },
      {
        title: 'Contratos por tipo de cliente y posesion',
        subtitle: `Actualizado: ${updatedAt} • Total contratos: ${totalContracts}`,
        columns: [
          { key: 'client_type', label: 'Tipo cliente', align: 'left' },
          { key: 'possession', label: 'Tipo posesion', align: 'left' },
          { key: 'count', label: 'Cantidad', align: 'center' },
        ],
        rows: reportResults.client_type_possession || [],
        chartId: 'gerenciales-contract-details-client-type-possession',
        totalLabel: 'Total contratos',
        totalCount: totalContracts,
      },
    ];
  };

  /**
   * Dibuja una tabla estilizada en el PDF.
   * @param {jsPDF} doc - Instancia de jsPDF.
   * @param {Object} section - Configuracion de la seccion.
   * @param {number} startX - Coordenada inicial X.
   * @param {number} startY - Coordenada inicial Y.
   * @param {number} tableWidth - Ancho de tabla.
   * @returns {number} Altura usada por la tabla.
   */
  const drawPdfTable = (doc, section, startX, startY, tableWidth) => {
    const rowHeight = 8;
    const columns = section.columns;
    const columnWidth = tableWidth / columns.length;
    let cursorY = startY;

    doc.setFillColor(204, 239, 208);
    doc.rect(startX, cursorY, tableWidth, rowHeight, 'F');
    doc.setTextColor(20, 20, 20);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    columns.forEach((column, index) => {
      const textX = startX + index * columnWidth + 2;
      doc.text(column.label, textX, cursorY + 5.5);
    });

    cursorY += rowHeight;
    doc.setFont('helvetica', 'normal');

    section.rows.forEach((row) => {
      doc.setDrawColor(210);
      doc.rect(startX, cursorY, tableWidth, rowHeight);

      columns.forEach((column, index) => {
        const value = row?.[column.key] != null ? String(row[column.key]) : '-';
        const textX = startX + index * columnWidth + 2;
        doc.text(value, textX, cursorY + 5.5);
      });

      cursorY += rowHeight;
    });

    doc.setDrawColor(210);
    doc.rect(startX, cursorY, tableWidth, rowHeight);
    columns.forEach((column, index) => {
      let value = '';
      if (index === 0) value = section.totalLabel;
      if (index === columns.length - 1) value = String(section.totalCount);
      const textX = startX + index * columnWidth + 2;
      doc.setFont('helvetica', 'bold');
      doc.text(value, textX, cursorY + 5.5);
    });
    doc.setFont('helvetica', 'normal');

    return cursorY + rowHeight - startY;
  };

  const handleDownloadPdf = async () => {
    if (reportType !== GERENCIALES_REPORT_TYPE) return;
    if (!hasReportData(results)) return;

    setIsDownloading(true);
    try {
      const sections = buildContractDetailsPdfSections(results, header);
      const doc = new jsPDF('landscape');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 10;

      for (let index = 0; index < sections.length; index += 1) {
        if (index > 0) doc.addPage();
        const section = sections[index];

        doc.setFillColor(59, 63, 70);
        doc.rect(margin, margin, pageWidth - margin * 2, 10, 'F');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(section.title, margin + 3, margin + 7);

        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        doc.text(section.subtitle, margin + 3, margin + 16);

        const tableX = margin;
        const tableY = margin + 20;
        const tableWidth = pageWidth * 0.42;
        const tableHeight = drawPdfTable(doc, section, tableX, tableY, tableWidth);

        const chartX = tableX + tableWidth + 8;
        const chartY = tableY;
        const chartWidth = pageWidth - chartX - margin;
        const chartHeight = Math.min(pageHeight - chartY - margin, tableHeight + 20);

        const chartPng = await getChartPng(section.chartId);
        if (chartPng) {
          doc.addImage(chartPng, 'PNG', chartX, chartY, chartWidth, chartHeight);
        }
      }

      doc.save('Estadisticas_Gerenciales_Contrato.pdf');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box>
      <TableTitleBox title="Estadisticas | Gerenciales" />
      <GerencialesFilters
        reportType={reportType}
        reportOptions={GERENCIALES_REPORT_OPTIONS}
        loading={loading}
        error={error}
        header={header}
        onReportTypeChange={handleReportTypeChange}
        onReload={reload}
        onDownloadPdf={handleDownloadPdf}
        disableDownload={!hasReportData(results) || isDownloading}
      />
      {reportType === GERENCIALES_REPORT_TYPE && (
        <ContractDetailsReport loading={loading} results={results} header={header} />
      )}
    </Box>
  );
};

export default GerencialesStatistics;
